import { useTranslation } from "react-i18next";
import { IconArrowUpRight } from "@tabler/icons-react";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import { useMarkdown } from "../../../../domains/app/hooks/useMarkdown";
import { useApiQuery } from "../../../../services/api";
import ContentMarkdown from "../../../../components/ContentMarkdown";
import { PolicyTextStyle } from "../../../../components/PolicyTextStyle";
import Loader from "../../../../components/Loader";
import { Tabs } from "../../../../components/ui/Tabs";
import { LEGAL_DOCS } from "../../../../components/settings/sheet/views/LegalView";
import { PartnerTermsContent } from "../../../../components/settings/partners/PartnerTermsContent";
import { PartnersList } from "../../../../components/partners/PartnersList";
import {
  openDesktopSettings,
  type DesktopSettingsPage,
} from "../../../../domains/app/stores/desktopSettingsStore";
import type { LegalDocTab } from "../../../../domains/app/stores/settingsSheetStore";
import { SettingsPage } from "../components/SettingsPanel";

/**
 * The terms and the privacy policy, and the partner half of each.
 *
 * The tab rides in the page (`{ page: "privacy", tab: "partners" }`) rather than
 * in component state, so `/settings/privacy?tab=partners`, which a consent email
 * links to, can open the modal straight on the partner opt-ins.
 */
export function LegalSection({
  doc,
  tab,
}: {
  doc: "terms" | "privacy";
  tab: LegalDocTab;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();

  const source = LEGAL_DOCS[doc];
  const localized = source[activeLang];
  const { content } = useMarkdown(
    localized === undefined ? source.en : localized
  );

  // This surface only renders for a signed-in reader, so /users/me is always
  // fetchable. It drives the Partners tab.
  const userQuery = useApiQuery("/webapp/private/users/me", {
    query: { language: activeLang },
    keepPreviousData: true,
  });
  // The partners of the rooms the reader joined, with their privacy policies:
  // a partner of the Partners tab opens its own when it has one.
  const sharingQuery = useApiQuery(
    "/webapp/private/users/me/partner-sharing",
    doc === "privacy" ? { query: { language: activeLang } } : false
  );

  // Literal t() calls: the i18n scanner cannot extract t(variable).
  const title = doc === "terms" ? t("Terms of use") : t("Privacy policy");

  // Parity with the mobile view: the terms only grow a Partners tab when one of
  // the reader's rooms ships partner T&Cs; the privacy policy always has one,
  // because its opt-ins tab carries its own empty state.
  const showTabs =
    doc === "privacy"
      ? true
      : userQuery.state === "success" &&
        userQuery.body.roomUserSettings.some(
          (setting) =>
            setting.room.termsConditions !== null &&
            setting.room.termsConditions.enabled === true
        );
  const activeTab: LegalDocTab = showTabs ? tab : "juisci";

  return (
    <SettingsPage back={null}>
      {/* Not a SettingsSection: the tabs sit under the title and their own
          line replaces the section's (Figma "D3.settings.terms"). */}
      <section className="flex flex-col">
        <h2 className="text-heading-xs font-semibold text-ds-slate-950">
          {title}
        </h2>
        {showTabs ? null : <div className="mt-2.5 h-px bg-ds-slate-100" />}
        {showTabs ? (
          <Tabs
            tabs={[
              { value: "juisci", label: "Juisci", badge: null, icon: null },
              { value: "partners", label: "Partners", badge: null, icon: null },
            ]}
            activeTab={activeTab}
            className="mt-2.5 border-t-0"
            onTabChange={(next) => {
              const page: DesktopSettingsPage = { page: doc, tab: next };
              openDesktopSettings(page);
            }}
          />
        ) : null}

        <div className="flex flex-col pt-4">
          {activeTab === "partners" ? (
            doc === "privacy" ? (
              // The choice lives in "Partner sharing": the Partners tab of
              // the privacy policy only lists the partners, as on a phone.
              <PartnersList
                layout="desktop"
                policies={
                  sharingQuery.state === "success"
                    ? {
                        organisationIds: new Set(
                          sharingQuery.body.partners
                            .filter(
                              (partner) => partner.privacyPolicies.length > 0
                            )
                            .map((partner) => partner.organisationId)
                        ),
                        onOpen: (organisationId) => {
                          openDesktopSettings({
                            page: "partnerPrivacy",
                            organisationId,
                          });
                        },
                      }
                    : null
                }
              />
            ) : userQuery.state === "success" ? (
              <PartnerTermsContent
                isLoading={userQuery.isFetching}
                roomUserSettings={userQuery.body.roomUserSettings}
              />
            ) : userQuery.state === "error" ? (
              <p className="text-sm text-ds-slate-700">{userQuery.message}</p>
            ) : (
              <Loader expanded />
            )
          ) : content === null ? (
            <Loader expanded />
          ) : (
            <PolicyTextStyle>
              <ContentMarkdown math={false} linkVariant="anchor">
                {content}
              </ContentMarkdown>
            </PolicyTextStyle>
          )}
        </div>
      </section>
    </SettingsPage>
  );
}

/**
 * The privacy policy of one partner, opened from the Partners tab of the
 * privacy policy: for each room of the partner that the reader joined, the
 * room's privacy notice and the link to the partner's own policy. A page of
 * the settings modal with its way back, never a dialog over it.
 */
export function PartnerPrivacySection({
  organisationId,
}: {
  organisationId: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const sharingQuery = useApiQuery("/webapp/private/users/me/partner-sharing", {
    query: { language: activeLang },
  });

  const partner =
    sharingQuery.state === "success"
      ? sharingQuery.body.partners.find(
          (candidate) => candidate.organisationId === organisationId
        )
      : undefined;

  return (
    <SettingsPage
      back={{
        label: t("Privacy policy"),
        onBack: () => {
          openDesktopSettings({ page: "privacy", tab: "partners" });
        },
      }}
    >
      {sharingQuery.state === "pending" ? (
        <Loader expanded />
      ) : partner === undefined ? (
        // The query failed, or the reader left the partner's last room.
        <p className="m-0 text-sm text-ds-slate-700">
          {t("Something went wrong. Please try again.")}
        </p>
      ) : (
        <section className="flex flex-col gap-2.5">
          <div className="flex items-center gap-3.5">
            <img
              src={partner.logoUrl}
              alt=""
              className="size-8 shrink-0 object-contain"
            />
            <h2 className="text-heading-xs font-semibold text-ds-slate-950">
              {partner.name}
            </h2>
          </div>
          <div className="h-px bg-ds-slate-100" />
          <div className="flex flex-col gap-5 pt-2">
            {partner.privacyPolicies.map((policy) => (
              <div key={policy.roomName} className="flex flex-col gap-1.5">
                <span className="text-desktop-meta text-ds-slate-500">
                  {policy.roomName}
                </span>
                {policy.description !== null ? (
                  <p className="m-0 text-sm text-ds-slate-700">
                    {policy.description}
                  </p>
                ) : null}
                <a
                  href={policy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 self-start text-sm font-medium text-ds-mint-700 hover:underline [&_svg]:size-4"
                >
                  {policy.label !== null ? policy.label : t("Privacy policy")}
                  <IconArrowUpRight stroke={1.5} />
                </a>
              </div>
            ))}
          </div>
        </section>
      )}
    </SettingsPage>
  );
}
